import { fillablePdf } from './fillable';
import { drawAgencyHeader } from './agencyInfo';

const L = 14;
const W = 188;
const BLUE = [30, 64, 175];

// Small builder for agency notices/profiles: every value is an editable PDF field
// theme 'color' (consent & admission forms): navy title band with a gold rule and teal / navy section bars
export const formPdf = (title, subtitle = '', { theme } = {}) => {
  const doc = fillablePdf({ unit: 'mm', format: 'letter' });
  doc.autoFill = false;
  let y = 30;
  const color = theme === 'color';
  let band = 0;
  const BAR = color ? [[15, 118, 110], [30, 58, 138]] : [BLUE];
  const BOX = color ? [236, 246, 244] : [232, 240, 254];
  drawAgencyHeader(doc, L, 4, { size: 12 });
  if (color) {
    doc.setFillColor(30, 58, 138); doc.rect(L, 19, W, 7.5, 'F');
    doc.setFillColor(212, 175, 55); doc.rect(L, 26.5, W, 0.9, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12.5); doc.setTextColor(255);
    doc.text(title, L + 2.5, 24.2);
    y = 31;
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...BLUE);
    doc.text(title, L, 24);
  }
  if (subtitle) { doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(color ? 255 : 90); doc.text(subtitle, L + W - (color ? 2.5 : 0), 24, { align: 'right' }); }
  doc.setTextColor(0);
  const ensure = (h) => { if (y + h > 262) { doc.addPage(); y = 18; } };

  const api = {
    doc,
    section: (t) => {
      ensure(12); y += 2;
      doc.setFillColor(...BAR[band % BAR.length]); band += 1; doc.rect(L, y, W, 6, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(255);
      doc.text(t, L + 2, y + 4.2); doc.setTextColor(0); y += 8;
    },
    // cells: [[label, value, widthFraction(0-1), {lines}]]
    row: (cells) => {
      const lines = Math.max(...cells.map(c => (c[3] && c[3].lines) || 1));
      const h = 4 + lines * 4.6;
      ensure(h + 1);
      let x = L;
      cells.forEach(([label, value, frac, o]) => {
        const w = W * frac;
        if (o?.bold) { doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...BLUE); } else { doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(80); }
        doc.text(label, x + 1, y + 3);
        doc.setFillColor(...BOX); doc.rect(x + 0.5, y + 3.8, w - 1.5, h - 4, 'F');
        doc.addFill({ type: 'text', x: x + 0.5, y: y + 3.8, w: w - 1.5, h: h - 4, value: value == null ? '' : String(value), multiline: lines > 1 || Boolean(o?.lines), fontSize: 9 });
        x += w;
      });
      doc.setTextColor(0);
      y += h + 1.5;
    },
    checks: (items) => {
      ensure(7);
      let x = L;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      items.forEach(([label, on]) => {
        const tw = doc.getTextWidth(label) + 10;
        if (x + tw > L + W) { x = L; y += 6; ensure(6); }
        doc.setDrawColor(...BLUE); doc.rect(x, y, 3.6, 3.6);
        doc.addFill({ type: 'check', x, y, w: 3.6, h: 3.6, value: Boolean(on) });
        doc.text(label, x + 5, y + 3);
        x += tw;
      });
      y += 7;
    },
    // keep at least h mm free on this page (no orphan section bars)
    space: (h) => ensure(h),
    // one checkbox with a long, wrapped label
    checkLong: (label, on) => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      const lines = doc.splitTextToSize(label, W - 6);
      ensure(lines.length * 4 + 3);
      doc.setDrawColor(...BLUE); doc.rect(L, y, 3.6, 3.6);
      doc.addFill({ type: 'check', x: L, y, w: 3.6, h: 3.6, value: Boolean(on) });
      doc.text(lines, L + 5, y + 3);
      y += lines.length * 4 + 3;
    },
    para: (t, o = {}) => {
      doc.setFont('helvetica', o.bold ? 'bold' : 'normal'); doc.setFontSize(o.size || 9); doc.setTextColor(...(o.color || [0, 0, 0]));
      const lines = doc.splitTextToSize(t, W);
      ensure(lines.length * 4 + 2);
      doc.text(lines, L, y + 3);
      doc.setTextColor(0);
      y += lines.length * 4 + 2;
    },
    // values[i]: { image: PNG data URL, text: printed value }
    signatures: (labels, values = []) => {
      const tall = values.some(v => v?.image);
      ensure(tall ? 24 : 16); y += tall ? 16 : 8;
      const w = W / labels.length;
      labels.forEach((lab, i) => {
        const x = L + i * w;
        const v = values[i] || {};
        if (v.image) doc.addImage(v.image, 'PNG', x, y - 15, Math.min(60, w - 8), 14);
        else doc.addFill({ type: 'text', x, y: y - 6, w: w - 6, h: 6, value: v.text || '', fontSize: 10 });
        doc.setDrawColor(0); doc.line(x, y, x + w - 6, y);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.text(lab, x, y + 3.5);
      });
      y += 8;
    },
  };
  return api;
};

export const finishPdf = (doc, label) => {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(110);
    doc.text(`${label}  |  Page ${i} of ${pages}`, L, 272);
    doc.setTextColor(0);
  }
  doc.fillMode = 'prefill';
  return doc;
};
